import {
    AmbientestLogbookRecord,
    ApiAmbientestLogbookRecordRequest,
    ApiBaseRepository,
    ApiBlogEntry,
    ApiCatalog,
    ApiCatalogGeneric,
    ApiCatalogRoot,
    ApiCredentials,
    ApiDashboardCatalog,
    ApiDocumentationComment,
    ApiDocumentationPage,
    ApiJiraCredentials,
    ApiJiraIssueItem,
    ApiJiraProject,
    ApiJiraTicketWorkflowData,
    ApiPinnedDashboard,
    ApiPortalAccessMenu,
    ApiPortalAccessRol,
    ApiPortalNotifications,
    ApiPortalUserBase,
    ApiPortalUserDataPart,
    ApiResource,
    ApiSartokenCodSegLogbook,
    ApiSartokenServiceLogbook,
    ApiSartokenServiceLogbookDetail,
    ApiServiceItem,
    ApiUsrDataPartCatalog,
    BigObject,
    BlogItem,
    CatalogGenericType,
    CatalogKeyType,
    CatalogRootType,
    CatalogType,
    CodeLogbookRecord,
    CredentialJiraType,
    CredentialType,
    DashboardCatalog,
    DashboardProject,
    DocPage,
    DocPageComment,
    IssueWorkflow,
    JiraIssue,
    NavItemDataProps,
    PinnedDashboard,
    RecordOption,
    RepositoryType,
    ResourceType,
    ResourceVersionType,
    SartokenLogbookRecord,
    SartokenLogbookRecordDetail,
    SearchItem,
    UrlSearchBuilder,
    UserData,
    UserDataPart,
    UserNotification,
    UserRolType,
    UsrDataPartCatalogType,
    WorkflowStatus,
    WorkflowTransition
} from "../types";
import { URLAPP } from "../config";
import businessHelper from "./business.helper";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

export const mapper = {
    apiCredentials: (apiCredential: ApiCredentials): CredentialType => ({
        id: apiCredential.id,
        key: apiCredential.acceso,
        secret: apiCredential.credencial,
        token: apiCredential.token,
        workspace: apiCredential.workspace,
        controlDate: apiCredential.fechaControl,
        description: apiCredential.descripcion,
        user: apiCredential.usuarioModificador,
        repositories: apiCredential.repositorios?.map(r => ({
            id: r.id,
            idWorkspace: apiCredential.id,
            subtype: r.subtipo,
            path: r.rutaRepositorio,
            name: r.nombre,
            description: r.descripcion
        }))
    }),
    apiCredentialsJira: (cred: ApiJiraCredentials): CredentialJiraType => {
        return ({
            controlDate: cred.fechaControl,
            description: cred.descripcion,
            id: cred.id,
            key: cred.acceso,
            secret: cred.credencial,
            token: cred.token,
            user: cred.usuarioModificador,
            workspace: cred.workspace,
            workspaceId: cred.workspaceId,
            dashboards: (cred.tableros || []).map(apiPinnedDashboard).map(d => ({ ...d, idCredential: cred.id }))
        })
    },
    apiJiraProject: (d: ApiJiraProject): DashboardProject => ({
        avatar: d.avatar,
        dashboardId: d.idTercero,
        key: d.key,
        name: d.nombre,
        type: d.tipo
    }),
    apiJiraWorkflow: (data: ApiJiraTicketWorkflowData): IssueWorkflow => {
        let transitions: WorkflowTransition[] = data?.transiciones.map(t => ({
            id: t.id,
            description: t.descripcion,
            name: t.nombre,
            reference: t.referencia,
            type: t.tipo
        }));
        let transitionOrder = transitions.map(t => t.reference);
        transitionOrder = transitionOrder.filter((t, pos) => transitionOrder.indexOf(t) === pos);
        let statuses: WorkflowStatus[] = data?.statues.map(s => ({
            id: s.id,
            category: s.categoria,
            name: s.nombre,
            reference: s.referencia,
            issues: s.issues?.map(apiJiraIssue)
        }));
        statuses.sort((a, b) => transitionOrder.indexOf(a.id) > transitionOrder.indexOf(b.id) ? 1 : -1);
        return {
            id: data?.id,
            transitions: transitions,
            status: statuses
        }
    },
    apiJiraIssue,
    apiPinnedDashboard,
    apiMenu: menuMapper,
    apiRol: (data: ApiPortalAccessRol): UserRolType => ({
        id: data.id,
        name: data.nombre,
        totalUsers: data.totalUsuarios,
        description: data.descripcion,
        menu: data.menu?.map(i => menuMapper(i))
    }),
    apiCatalog: mapCatalog,
    toApiCatalog: (data: CatalogType): ApiCatalog => ({
        activo: data.active ? 1 : 0,
        codigo: String(data.value),
        valor: data.label,
        operativo: data.initial ? 1 : 0,
        tipo: data.type
    }),
    apiCatalogGeneric: (data: ApiCatalogGeneric): CatalogGenericType => {
        return {
            usrDataPart: data.usrDataPart?.map(mapper.apiUsrDataPartCatalog).sort((a, b) => a.order - b.order),
            ticketModuloOp: data.ticketModuloOp?.map(mapper.apiDashboardCatalog).sort((a, b) => a.id - b.id),
            ticketProcesoOp: data.ticketProcesoOp?.map(mapper.apiDashboardCatalog).sort((a, b) => a.id - b.id),
        }
    },
    apiUsrDataPartCatalog: (data: ApiUsrDataPartCatalog | unknown): UsrDataPartCatalogType => ({
        active: (data as ApiUsrDataPartCatalog).activo === 1,
        code: (data as ApiUsrDataPartCatalog).clave,
        description: (data as ApiUsrDataPartCatalog).descripcion,
        label: (data as ApiUsrDataPartCatalog).mapeo,
        icon: (data as ApiUsrDataPartCatalog).icono as IconProp,
        order: (data as ApiUsrDataPartCatalog).orden
    }),
    toApiUsrDataPartCatalog: (data: UsrDataPartCatalogType): ApiUsrDataPartCatalog => ({
        activo: data.active ? 1 : 0,
        clave: data.code,
        descripcion: data.description,
        icono: data.icon as string,
        mapeo: data.label,
        orden: data.order
    }),
    apiDashboardCatalog: (data: ApiDashboardCatalog | unknown): DashboardCatalog => ({
        id: (data as ApiDashboardCatalog).id,
        description: (data as ApiDashboardCatalog).descripcion,
        name: (data as ApiDashboardCatalog).nombre,
        active: !!(data as ApiDashboardCatalog).activo
    }),
    toApiDashboardCatalog: (data: DashboardCatalog): ApiDashboardCatalog => ({
        id: data.id,
        descripcion: data.description,
        nombre: data.name,
        activo: data.active ? 0 : 1,
    }),
    apiUser: (data: ApiPortalUserBase): UserData => ({
        id: data.id,
        name: data.nombre,
        rol: data.rol,
        particularData: (data.datosParticulares || []).map(mapUserDataPart)
    }),
    apiUserInfo: mapUserDataPart,
    apiNotifications: (data: ApiPortalNotifications): UserNotification => ({
        id: data.id,
        body: data.contenido,
        date: data.fechaControl,
        origin: data.origen,
        status: data.estatus,
        icon: 'bell',
        label: ''
    }),
    apiCatalogRoot: (apiCatalog: ApiCatalogRoot) => {
        let catalogs: any = {}
        for (let prop in apiCatalog) {
            catalogs[prop] = apiCatalog[prop as CatalogKeyType].map(mapCatalog)
        }
        return catalogs as CatalogRootType
    },
    apiResource: mapApiResource,
    apiRepository: (repository: ApiBaseRepository) => ({
        id: repository.id,
        label: repository.nombre,
        resources: (repository.recursos || []).map(mapApiResource)
    }),
    apiResourceList: (repository: ApiBaseRepository): ResourceType[] => ((repository.recursos || []).map(r => ({ ...mapApiResource(r), idRepository: repository.id }))),
    apiDocumentPage: (data: ApiDocumentationPage, id: number): DocPage => {
        return {
            id,
            content: businessHelper.formatImages(data.contenido),
            updatedAt: data.actualizacion,
            version: data.version,
            comments: data.comentarios.map(mapDocumentationComment)
        }
    },
    apiDocumentComment: mapDocumentationComment,
    apiBlogEntry: (data: ApiBlogEntry): BlogItem => ({
        author: data.autor,
        date: data.fechaControl,
        id: data.id,
        keywords: data.keywords.split(','),
        description: data.sinopsis,
        title: data.titulo,
        icon: 'blog',
        link: data.url,
        withComments: data.comentariosHabilitados,
        withIndex: data.indiceHabilitado
    }),
    apiLogbookService: (data: ApiServiceItem): RecordOption => ({
        label: `${data.descripcion} (${data.nombre})`,
        id: data.id + '',
        value: data.nombre || ''
    }),
    apiAmbientestLogbook: (r: ApiAmbientestLogbookRecordRequest): AmbientestLogbookRecord => {
        let envTypeParts = r.tipoSolicitud.split('/')
        let plainEnvType = envTypeParts[envTypeParts.length - 1]
        let urlEnvType = ''
        for (let k of Object.keys(URLAPP)) {
            let url = (URLAPP as BigObject<string>)[k]
            if (url.endsWith(`/${plainEnvType}`)) {
                urlEnvType = url
            }
        }
        return {
            curp: r.curp,
            date: r.fechaSolicitud,
            envType: { label: plainEnvType, value: urlEnvType },
            id: r.id,
            message: r.mensaje,
            status: r.estatus === 1 ? 'Exitosa' : 'Fallida',
            userName: r.usuario
        }
    },
    apiSartokenSmsLogbook: (r: ApiSartokenCodSegLogbook): CodeLogbookRecord => ({
        codeValue: r.codigo,
        createdAt: r.fechaGeneracion,
        id: r.idCodigo,
        idApplication: r.idAplicacion,
        sendType: r.tipoEnvio,
        status: r.estatus,
        updatedAt: r.fechaControl,
        target: r.tipoEnvio === 2 ? businessHelper.hideEmail(r.email) : businessHelper.hideCharacters(r.celular)
    }),
    apiSartokenServiceLogbook: (r: ApiSartokenServiceLogbook): SartokenLogbookRecord => {
        let idProyectSplit = r.identidadProyecto?.split('.') || ['X']
        return {
            curp: r.curp,
            date: r.fecha,
            device: {
                appVersion: r.dispositivo.appVersion,
                model: r.dispositivo.modelo,
                name: r.dispositivo.nombre,
                os: r.dispositivo.os,
                sdkVersion: r.dispositivo.sdkVersion,
                uuid: r.dispositivo.uuid
            },
            exception: r.respExcepcion,
            id: Number(r.idBitacora),
            idClient: r.identidadCliente,
            idProyect: idProyectSplit[idProyectSplit.length - 1],
            message: r.respMensaje,
            serviceId: r.servicioId
        }
    },
    apiSartokenServiceLogbookDetail: (r: ApiSartokenServiceLogbookDetail): SartokenLogbookRecordDetail => ({
        request: businessHelper.hidePersonalDataOnJson(r.request || ''),
        response: businessHelper.hidePersonalDataOnJson(r.response || r.stackTrace || '')
    }),
    menuListToArray,
    toSearchApidoc: (keywords: string[], urlBuilder: UrlSearchBuilder<ResourceType>, urlParam: string) => (item: ResourceType): SearchItem => ({
        title: item.description,
        description: `${item.name} | ${item.description} <br/> ${item.versions[0]?.artifacts[0]?.path}`,
        keywords,
        ...urlBuilder(item, urlParam)
    }),
    toSearchResource: (keywords: string[], urlBuilder: UrlSearchBuilder<ResourceType>, urlParam: string) => (item: ResourceType): SearchItem => ({
        title: item.label,
        description: `${item.name} | Versiones: ${item.versions.length} <br/>  ${item.description}`,
        keywords: [...(new Set(keywords))],
        ...urlBuilder(item, urlParam)
    }),
    toSearchResourceVersion: (keywords: string[], urlBuilder: UrlSearchBuilder<ResourceVersionType>, version: ResourceVersionType, urlParam: string) => (item: ResourceType): SearchItem => ({
        title: item.label,
        description: `${item.name} | Version: ${version.version} | ${version.note}  <br/>  ${item.description}`,
        keywords,
        ...urlBuilder(version, urlParam)
    }),
    toSearchCredential: (keywords: string[], urlBuilder: UrlSearchBuilder<CredentialType>) => (item: CredentialType): SearchItem => ({
        title: `Workspace ${item.workspace}`,
        description: `${item.user} | Repositorios: ${item.repositories.length}`,
        keywords,
        ...urlBuilder(item)
    }),
    toSearchRepository: (keywords: string[], urlBuilder: UrlSearchBuilder<CredentialType>, repository: RepositoryType) => (item: CredentialType): SearchItem => ({
        title: `Workspace ${item.workspace} | Repositorio ${repository.name}`,
        description: `${repository.description} | Tipo: ${repository.subtype} <br/> Path: ${repository.path}`,
        keywords,
        ...urlBuilder(item)
    }),
    toSearchCatalog: (keywords: string[], urlBuilder: UrlSearchBuilder<CatalogType>, urlParam: string) => (item: CatalogType): SearchItem => ({
        title: `Catalogo ${urlParam}`,
        description: `${keywords[0]} | ${item.value} | ${item.label}`,
        keywords,
        ...urlBuilder(item, urlParam)
    }),
    toSearchRolMenu: (keywords: string[], urlBuilder: UrlSearchBuilder<UserRolType>, urlParam: string) => (item: UserRolType): SearchItem => ({
        title: `Rol ${item.name}`,
        description: `Rol de acceso | Usuarios: ${item.totalUsers}`,
        keywords,
        ...urlBuilder(item, urlParam)
    })
}

function mapDocumentationComment(data: ApiDocumentationComment): DocPageComment {
    return {
        id: data.id,
        content: data.contenido,
        updatedAt: data.actualizacion,
        user: data.usuario,
        version: data.version,
        comments: data.comentarios.map(mapDocumentationComment)
    }
}

function mapUserDataPart(data: ApiPortalUserDataPart): UserDataPart {
    return {
        id: data.id,
        code: data.clave,
        label: data.etiqueta,
        icon: data.icono as IconProp,
        order: data.orden,
        value: data.valor
    }
}

function mapCatalog(o: ApiCatalog | unknown): CatalogType {
    return {
        active: !!(o as ApiCatalog).activo,
        label: (o as ApiCatalog).valor,
        value: (o as ApiCatalog).codigo,
        initial: !!(o as ApiCatalog).operativo
    }
}

function menuMapper(item: ApiPortalAccessMenu, parentItem?: NavItemDataProps): NavItemDataProps {
    let navItem: NavItemDataProps = {
        id: item.id,
        label: item.nombre,
        path: item.ruta,
        active: item.activo,
        icon: item.icono as IconProp,
        description: item.descripcion,
        breadcrumbs: []
    }
    let itemBreadCrumb = { label: navItem.label, path: navItem.path || URLAPP.root }
    navItem.breadcrumbs = parentItem ? [...parentItem.breadcrumbs, itemBreadCrumb] : [itemBreadCrumb]
    if (item.submenus && item.submenus.length > 0) {
        navItem.items = item.submenus?.map(i => menuMapper(i, navItem))
    }
    return navItem
}


function menuListToArray(data: NavItemDataProps[], idRoot: number) {
    let list: NavItemDataProps[] = []
    if (data) {
        for (let item of data) {
            list = [...list, ...menuToArray(item, idRoot, [])]
        }
    }
    return list.sort((a, b) => (a.idRoot || 0) - (b.idRoot || 0))
}

function menuToArray(data: NavItemDataProps, idRoot: number, list: NavItemDataProps[]) {
    if (data.items?.length) {
        data.items.forEach(i => menuToArray(i, data.id, list))
    }
    list.push({ id: data.id, label: data.label, idRoot, path: data.path, breadcrumbs: data.breadcrumbs })
    return list
}

function mapApiResource(r: ApiResource): ResourceType {
    const timeReplace = 'T00:00:00'
    let data: ResourceType = {
        id: r.id,
        idRepository: r.idRepositorio,
        label: r.etiqueta,
        name: r.nombre,
        description: r.descripcion || '',
        createdAt: r.fechaCreacion?.replace(timeReplace, ''),
        updatedAt: r.fechaControl?.replace(timeReplace, ''),
        versions: ordenarVersionesDescendente(r.versiones.map(v => ({
            id: v.id,
            version: v.version,
            afores: v.afores.split(","),
            note: v.nota,
            normative: v.normativo,
            createdAt: v.fechaCreacion?.replace(timeReplace, ''),
            updatedAt: v.fechaControl?.replace(timeReplace, ''),
            artifacts: v.artefactos.map(a => ({
                id: a.id,
                name: a.nombre,
                type: a.tipoArtefacto,
                path: a.rutaArtefacto
            }))
        }))),
        linkedMenus: r.menuAsociado,
        linkedComments: r.comentariosAsociados?.toString()
    }
    if (r.ultimaVersion && r.versiones.findIndex(v => v.id === r.ultimaVersion?.id) === -1) {
        data.lastVersion = {
            id: r.ultimaVersion.id,
            note: r.ultimaVersion.nota,
            version: r.ultimaVersion.version,
            createdAt: r.ultimaVersion.fechaCreacion
        }
    }
    data.updatedAt = data.versions[0]?.updatedAt
    return data
}

function apiPinnedDashboard(d: ApiPinnedDashboard): PinnedDashboard {
    return ({
        avatar: d.avatar,
        controlDate: d.fechaControl,
        dashboardId: d.idTercero,
        description: d.descripcion,
        id: d.id,
        idCredential: d.idCredencial,
        ticketType: d.tipo,
        title: d.nombre,
        user: d.usuarioModificador
    })
}

function apiJiraIssue(r: ApiJiraIssueItem): JiraIssue {
    return {
        id: r.id,
        key: r.key,
        description: r.descripcion,
        label: r.label,
        projectId: r.idProyecto,
        summary: r.resumen,
        status: {
            id: r.estatus?.id,
            label: r.estatus?.label,
            color: r.estatus?.color?.split('-')[r.estatus?.color.split('-').length - 1]
        },
        created: r.creado,
        updated: r.actualizado,
        attachments: r.adjuntos?.map(a => ({
            id: a.id,
            name: a.nombre,
            type: a.tipoArtefacto,
            controlDate: a.fechaControl
        })),
        comments: r.comentarios?.map(a => ({
            id: a.id,
            text: a.text,
            media: a.media?.map(a => ({
                id: a.id,
                name: a.nombre,
                type: a.tipoArtefacto,
                controlDate: a.fechaControl
            })),
            author: a.autor,
            created: a.creado,
            updated: a.actualizado
        }))
    }
}

function ordenarVersionesDescendente(versiones: ResourceVersionType[]) {
  return versiones.sort((a, b) => {
    const vA = String(a.version).split('.').map(Number);
    const vB = String(b.version).split('.').map(Number);

    for (let i = 0; i < 4; i++) {
      const numA = vA[i] || 0;
      const numB = vB[i] || 0;

      if (numA !== numB) {
        return numB - numA;
      }
    }

    return 0;
  });
}